import { createApi } from "@reduxjs/toolkit/query/react";
import { retryUnlessClientError } from "../retryConditions";
import { createRetryingBaseQuery } from "../retryingBaseQuery";

/**
 * mfaAPI wraps the three backend endpoints that implement local (non-
 * Cognito) TOTP MFA:
 *
 *   POST /api/mfa/enroll/init    — generates TOTP secret, returns
 *                                  otpauth URL + base32 secret for QR
 *   POST /api/mfa/enroll/verify  — confirms enrollment with first code;
 *                                  returns {step_up_token, expires_in_seconds}
 *   POST /api/mfa/step-up/verify — action-gating TOTP verification;
 *                                  returns {step_up_token, expires_in_seconds}
 *
 * Cloud (Cognito) deployments do NOT hit these endpoints — they use
 * Amplify's Auth.signIn / Auth.confirmSignIn to advance the Cognito
 * `auth_time` claim, which the backend's RequireFreshMFA middleware
 * reads uniformly. See AuthenticationTOTP.tsx for the selection logic.
 */

const maxRetries = 2;

const baseQuery = createRetryingBaseQuery(
  process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "",
  maxRetries,
);

export const mfaAPI = createApi({
  reducerPath: "mfaAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [],
  endpoints: (builder) => ({
    postMfaEnrollInit: builder.mutation({
      query: (body: any) => ({
        url: `/mfa/enroll/init`,
        method: "POST",
        body: body?.body ?? {},
        headers: { authorization: body?.token ?? "" },
      }),
      // A re-enrollment carries the current TOTP code (LIFT-1085). A wrong one
      // gets a 401 that no retry can fix; retrying would only resend it.
      extraOptions: { retryCondition: retryUnlessClientError(maxRetries) },
    }),
    postMfaEnrollVerify: builder.mutation({
      query: (body: any) => ({
        url: `/mfa/enroll/verify`,
        method: "POST",
        body: body?.body ?? {},
        headers: { authorization: body?.token ?? "" },
      }),
    }),
    postMfaStepUpVerify: builder.mutation({
      query: (body: any) => ({
        url: `/mfa/step-up/verify`,
        method: "POST",
        body: body?.body ?? {},
        headers: { authorization: body?.token ?? "" },
      }),
    }),
    postMfaStepUpPasswordVerify: builder.mutation({
      query: (body: any) => ({
        url: `/mfa/step-up/password-verify`,
        method: "POST",
        body: body?.body ?? {},
        headers: { authorization: body?.token ?? "" },
      }),
    }),
  }),
});

export const {
  usePostMfaEnrollInitMutation,
  usePostMfaEnrollVerifyMutation,
  usePostMfaStepUpVerifyMutation,
  usePostMfaStepUpPasswordVerifyMutation,
} = mfaAPI;

export default mfaAPI;
